/**
 * Agent-run-state bridge.
 *
 * Makes the live Rovo agent run-state readable outside Rovo chat, keyed by the run's `conversationId`
 * (its true identity) and carrying the invoking agent-mention node's `localId` as attribution. Any
 * surface holding a conversationId reads it directly; the editor reads by node via the localId the
 * record carries.
 *
 * Producer: ai-mate's chat/message stream processing calls the imperative functions below as a run
 * moves through its lifecycle, all keyed by `conversationId`. A run is only tracked once
 * {@link beginAgentRun} has seeded a record for it at send-time; {@link notifyAgentRunStateForConversation}
 * is a no-op for any conversation with no seeded record, so the bridge is inert unless a run seeded
 * it (only agent-mention runs do, behind the experiment).
 *
 * Consumer: {@link useAgentRunStateForConversation} / {@link useAgentRunStateForNode} / {@link useAgentRuns}
 * read reactively. Seeding sits behind the `platform_editor_agent_state_bridge` experiment, so the
 * whole bridge is dormant when it is off.
 */
import {
	createStateHook,
	createStore,
	defaultRegistry,
	type StoreActionApi,
} from 'react-sweet-state';

/**
 * The bridge's contract, normalised from the raw chat/message stream signals:
 * - `thinking`    ← agent reasoning before it acts
 * - `working`     ← run started / agent decided what to do
 * - `editing`     ← content streaming into the document
 * - `needs-input` ← the turn ended returning an action awaiting the user's confirmation
 * - `done`        ← the turn completed
 * - `failed`      ← the run errored
 * - `blocked`     ← a safety guard blocked the run
 */
export type AgentRunState =
	| 'thinking'
	| 'working'
	| 'editing'
	| 'needs-input'
	| 'done'
	| 'failed'
	| 'blocked';

/** A run's live state plus attribution, keyed in the store by its `conversationId`. */
export type AgentRunRecord = {
	state: AgentRunState;
	/** localId of the agent-mention node that invoked this run, when it came from a mention. */
	invokedByNodeLocalId?: string;
};

export type AgentRunRecordByConversationId = { [conversationId: string]: AgentRunRecord };

/**
 * States that end the run's turn. Once a record reaches one, later emits on the same conversation are
 * ignored so a trailing backend event can't revive it. `needs-input` is excluded: the run pauses for
 * the user and resumes on the same conversation, so a follow-up state must still apply.
 */
const TURN_END_STATES: ReadonlySet<AgentRunState> = new Set<AgentRunState>([
	'done',
	'failed',
	'blocked',
]);

/**
 * States where a turn is in flight. A run left in one of these when its stream ends (abort, timeout,
 * dropped request) never settled and should be cleared. `needs-input` is a valid resting state, so
 * it is not here.
 */
const ACTIVE_STATES: ReadonlySet<AgentRunState> = new Set<AgentRunState>([
	'thinking',
	'working',
	'editing',
]);

type State = {
	runByConversationId: AgentRunRecordByConversationId;
};

const store = createStore({
	name: 'agentMentionRunState',
	initialState: {
		runByConversationId: {},
	} as State,
	actions: {
		setRun:
			(conversationId: string, record: AgentRunRecord) =>
			({ setState, getState }: StoreActionApi<State>): void => {
				setState({
					runByConversationId: { ...getState().runByConversationId, [conversationId]: record },
				});
			},
		clearRun:
			(conversationId: string) =>
			({ setState, getState }: StoreActionApi<State>): void => {
				const next = { ...getState().runByConversationId };
				delete next[conversationId];
				setState({ runByConversationId: next });
			},
	},
});

const getRun = (conversationId: string): AgentRunRecord | undefined =>
	defaultRegistry.getStore(store).storeState.getState().runByConversationId[conversationId];

const applyRun = (conversationId: string, record: AgentRunRecord): void => {
	// Temporary demo line: proves run-state reaches the bridge, keyed by conversation and attributed
	// to its node. Only fires for mention-invoked runs the experiment seeded (an unseeded conversation
	// never reaches here), so it is dormant when platform_editor_agent_state_bridge is off. Remove
	// once a real consumer lands.
	// eslint-disable-next-line no-console
	console.log(
		`[agent-run-state] conversation ${conversationId} (node ${record.invokedByNodeLocalId ?? 'n/a'}) → ${record.state}`,
	);
	defaultRegistry.getStore(store).actions.setRun(conversationId, record);
};

/**
 * Seed a run and mark it started (`working`). Call at the send boundary, once both the invoking node
 * `localId` and the run's `conversationId` are known.
 *
 * A node has one active run at a time, so any earlier record it invoked is dropped first. That keeps
 * one record per node and stops the by-node lookup resolving to a stale earlier run.
 */
export const beginAgentRun = (conversationId: string, localId: string): void => {
	if (!conversationId || !localId) {
		return;
	}
	const runs = defaultRegistry.getStore(store).storeState.getState().runByConversationId;
	Object.keys(runs).forEach((priorConversationId) => {
		if (
			priorConversationId !== conversationId &&
			runs[priorConversationId].invokedByNodeLocalId === localId
		) {
			defaultRegistry.getStore(store).actions.clearRun(priorConversationId);
		}
	});
	applyRun(conversationId, { state: 'working', invokedByNodeLocalId: localId });
};

/**
 * Emit a run-state for a conversation. No-op when the conversation has no seeded record (i.e. the run
 * was not started from an agent mention) or its turn has already ended.
 */
export const notifyAgentRunStateForConversation = (
	conversationId: string,
	runState: AgentRunState,
): void => {
	const current = getRun(conversationId);
	if (!current || TURN_END_STATES.has(current.state)) {
		return;
	}
	applyRun(conversationId, { ...current, state: runState });
};

/** Forget a run (e.g. the turn was cancelled). */
export const endAgentRun = (conversationId: string): void => {
	if (!conversationId) {
		return;
	}
	defaultRegistry.getStore(store).actions.clearRun(conversationId);
};

/**
 * Clear a run only if it is still in flight. Call when a turn's stream ends by any path (abort,
 * timeout, dropped request) so an unsettled run does not stay `working`/`editing` forever. A resting
 * (`needs-input`) or terminal run is left untouched.
 */
export const endActiveAgentRun = (conversationId: string): void => {
	const current = getRun(conversationId);
	if (current && ACTIVE_STATES.has(current.state)) {
		defaultRegistry.getStore(store).actions.clearRun(conversationId);
	}
};

// Some invocation paths insert the prompt as a draft, so the user sends it manually and the invoking
// node localId can't ride the send call. Stash it when the chat is opened; the send action consumes
// it when the run actually starts.
let pendingInvocationLocalId: string | null = null;

/** Record the invoking node localId for the next send in any conversation. */
export const stashAgentRunInvocation = (localId: string): void => {
	pendingInvocationLocalId = localId || null;
};

/** Take and clear the stashed invoking node localId, if any. */
export const consumeAgentRunInvocation = (): string | null => {
	const localId = pendingInvocationLocalId;
	pendingInvocationLocalId = null;
	return localId;
};

// Wrapped in explicitly-typed functions so the public API satisfies --isolatedDeclarations.
const useRunsInternal = createStateHook(store, {
	selector: (state: State): AgentRunRecordByConversationId => state.runByConversationId,
});

const useRunStateForConversationInternal = createStateHook(store, {
	selector: (state: State, conversationId: string): AgentRunState | undefined =>
		state.runByConversationId[conversationId]?.state,
});

const useRunStateForNodeInternal = createStateHook(store, {
	selector: (state: State, localId: string): AgentRunState | undefined =>
		Object.values(state.runByConversationId).find((r) => r.invokedByNodeLocalId === localId)?.state,
});

/** Reactively read the whole `conversationId → run record` map (e.g. to group by node or agent). */
export const useAgentRuns = (): AgentRunRecordByConversationId => useRunsInternal();

/** Reactively read the run-state for a single `conversationId`. */
export const useAgentRunStateForConversation = (
	conversationId: string,
): AgentRunState | undefined => useRunStateForConversationInternal(conversationId);

/** Reactively read the run-state for the run invoked by a given mention node `localId`. */
export const useAgentRunStateForNode = (localId: string): AgentRunState | undefined =>
	useRunStateForNodeInternal(localId);
